<template>
  <main-template>
    <!-- Header -->
    <v-row>
      <v-col class="d-flex align-center">
        <v-btn icon class="mr-2" @click="$router.push({ name: 'codigosHabilidades.index' })">
          <v-icon>mdi-arrow-left</v-icon>
        </v-btn>
        <span class="text-h5"> {{ editing ? "Editar" : "Criar" }} Códigos e Habilidades </span>
      </v-col>
    </v-row>

    <v-row>
      <v-col>
        <v-stepper v-model="step" class="elevation-1">
          <!-- Stepper header -->
          <v-stepper-header>
            <v-stepper-step :complete="step > 1" step="1" color="teal darken-1">
              Série
            </v-stepper-step>
            <v-divider />
            <v-stepper-step :complete="step > 2" step="2" color="teal darken-1">
              Disciplina
            </v-stepper-step>
            <v-divider />
            <v-stepper-step :complete="step > 3" step="3" color="teal darken-1">
              Etapa
            </v-stepper-step>
            <v-divider />
            <v-stepper-step :complete="step > 4" step="4" color="teal darken-1">
              Itens
            </v-stepper-step>
          </v-stepper-header>

          <!-- Step 1: Série -->
          <v-stepper-content step="1">
            <ValidationObserver ref="obsStep1">
              <v-card flat class="mb-4">
                <v-card-text>
                  <p class="text-subtitle-1 font-weight-medium mb-4">
                    Selecione a série (turma escolar)
                  </p>
                  <ValidationProvider name="Série" rules="required" v-slot="{ errors }">
                    <v-autocomplete
                      v-model="form.serie"
                      :items="series"
                      item-text="descricao"
                      item-value="id"
                      label="Série *"
                      outlined
                      return-object
                      :loading="carregandoSeries"
                      no-data-text="Nenhuma série encontrada"
                      clearable
                      :error-messages="errors"
                      hide-details="auto"
                      @change="onSerieChange"
                    />
                  </ValidationProvider>
                </v-card-text>
                <v-card-actions>
                  <v-spacer />
                  <v-btn color="teal darken-1" dark @click="avancarStep1">
                    Próximo
                    <v-icon right>mdi-chevron-right</v-icon>
                  </v-btn>
                </v-card-actions>
              </v-card>
            </ValidationObserver>
          </v-stepper-content>

          <!-- Step 2: Disciplina -->
          <v-stepper-content step="2">
            <ValidationObserver ref="obsStep2">
              <v-card flat class="mb-4">
                <v-card-text>
                  <p class="text-subtitle-1 font-weight-medium mb-1">Selecione a disciplina</p>
                  <p class="text-caption grey--text mb-4">
                    Disciplinas vinculadas à série:
                    <strong>{{ form.serie && form.serie.descricao }}</strong>
                  </p>
                  <ValidationProvider name="Disciplina" rules="required" v-slot="{ errors }">
                    <v-autocomplete
                      v-model="form.disciplina"
                      :items="disciplinas"
                      item-text="descricao"
                      item-value="id"
                      label="Disciplina *"
                      outlined
                      return-object
                      :loading="carregandoDisciplinas"
                      no-data-text="Nenhuma disciplina vinculada a esta série"
                      clearable
                      :error-messages="errors"
                      hide-details="auto"
                      @change="onDisciplinaChange"
                    />
                  </ValidationProvider>
                </v-card-text>
                <v-card-actions>
                  <v-btn text @click="step = 1">
                    <v-icon left>mdi-chevron-left</v-icon>
                    Voltar
                  </v-btn>
                  <v-spacer />
                  <v-btn color="teal darken-1" dark @click="avancarStep2">
                    Próximo
                    <v-icon right>mdi-chevron-right</v-icon>
                  </v-btn>
                </v-card-actions>
              </v-card>
            </ValidationObserver>
          </v-stepper-content>

          <!-- Step 3: Etapa do circuito -->
          <v-stepper-content step="3">
            <ValidationObserver ref="obsStep3">
              <v-card flat class="mb-4">
                <v-card-text>
                  <p class="text-subtitle-1 font-weight-medium mb-1">
                    Selecione a etapa do circuito de notas
                  </p>
                  <p class="text-caption grey--text mb-4">
                    Bimestre ao qual estas habilidades se aplicam
                  </p>
                  <ValidationProvider name="Etapa" rules="required" v-slot="{ errors }">
                    <v-autocomplete
                      v-model="form.etapa"
                      :items="etapas"
                      item-text="descricao"
                      item-value="id"
                      label="Etapa *"
                      outlined
                      return-object
                      :loading="carregandoEtapas"
                      no-data-text="Nenhuma etapa de entrada disponível"
                      clearable
                      :error-messages="errors"
                      hide-details="auto"
                    />
                  </ValidationProvider>

                  <v-alert v-if="erroUnicidade" type="error" dense outlined class="mt-3">
                    {{ erroUnicidade }}
                  </v-alert>
                </v-card-text>
                <v-card-actions>
                  <v-btn text @click="step = 2">
                    <v-icon left>mdi-chevron-left</v-icon>
                    Voltar
                  </v-btn>
                  <v-spacer />
                  <v-btn
                    color="teal darken-1"
                    dark
                    :loading="verificandoUnicidade"
                    @click="avancarParaItens"
                  >
                    Próximo
                    <v-icon right>mdi-chevron-right</v-icon>
                  </v-btn>
                </v-card-actions>
              </v-card>
            </ValidationObserver>
          </v-stepper-content>

          <!-- Step 4: Itens -->
          <v-stepper-content step="4">
            <ValidationObserver ref="obsStep4">
              <v-card flat class="mb-4">
                <v-card-text>
                  <div class="d-flex align-center mb-2">
                    <p class="text-subtitle-1 font-weight-medium mb-0">Cadastre os itens</p>
                    <v-spacer />
                    <v-chip md :color="isInfantil ? 'teal' : 'blue'" dark label>
                      {{
                        isInfantil
                          ? "Educação Infantil — 2 níveis"
                          : "Ensino Fundamental — 3 níveis"
                      }}
                    </v-chip>
                  </div>

                  <!-- Context summary -->
                  <v-sheet
                    color="teal lighten-5"
                    rounded
                    class="pa-3 mb-4 d-flex flex-wrap"
                    style="gap: 16px"
                  >
                    <div>
                      <span class="text-caption grey--text text--darken-1">Série</span>
                      <div class="text-body-2 font-weight-medium">
                        {{ form.serie && form.serie.descricao }}
                      </div>
                    </div>
                    <div>
                      <span class="text-caption grey--text text--darken-1">Disciplina</span>
                      <div class="text-body-2 font-weight-medium">
                        {{ form.disciplina && form.disciplina.descricao }}
                      </div>
                    </div>
                    <div>
                      <span class="text-caption grey--text text--darken-1">Etapa</span>
                      <div class="text-body-2 font-weight-medium">
                        {{ form.etapa && form.etapa.descricao }}
                      </div>
                    </div>
                  </v-sheet>

                  <itens-tree-editor
                    v-model="form.itens"
                    :is-infantil="isInfantil"
                    :editing="editing"
                  />

                  <v-alert v-if="errosDuplicatas.length" type="error" dense outlined class="mt-3">
                    <div class="font-weight-medium mb-1">Itens com código duplicado:</div>
                    <ul class="ma-0 pl-4">
                      <li v-for="(e, i) in errosDuplicatas" :key="i">{{ e }}</li>
                    </ul>
                  </v-alert>
                  <v-alert
                    v-if="errosConflitosGlobais.length"
                    type="error"
                    dense
                    outlined
                    class="mt-3"
                  >
                    <div class="font-weight-medium mb-1">
                      Códigos já utilizados em outras configurações:
                    </div>
                    <ul class="ma-0 pl-4">
                      <li v-for="(c, i) in errosConflitosGlobais" :key="i">{{ c }}</li>
                    </ul>
                  </v-alert>
                  <v-alert v-if="erroItens" type="error" dense outlined class="mt-3">
                    {{ erroItens }}
                  </v-alert>
                </v-card-text>
                <v-card-actions>
                  <v-btn text @click="step = 3">
                    <v-icon left>mdi-chevron-left</v-icon>
                    Voltar
                  </v-btn>
                  <v-spacer />
                  <v-btn color="success" :loading="salvando" @click="salvar">
                    <v-icon left>mdi-content-save</v-icon>
                    {{ editing ? "Salvar alterações" : "Criar configuração" }}
                  </v-btn>
                </v-card-actions>
              </v-card>
            </ValidationObserver>
          </v-stepper-content>
        </v-stepper>
      </v-col>
    </v-row>
  </main-template>
</template>

<script>
import ItensTreeEditor from "./ItensTreeEditor.vue";

export default {
  components: { ItensTreeEditor },

  props: {
    editing: { type: Boolean, default: false },
    grupoId: { type: Number, default: null },
  },

  data() {
    return {
      step: 1,
      form: {
        serie: null,
        disciplina: null,
        etapa: null,
        itens: [],
      },
      series: [],
      disciplinas: [],
      etapas: [],
      carregandoSeries: false,
      carregandoDisciplinas: false,
      carregandoEtapas: false,
      verificandoUnicidade: false,
      salvando: false,
      erroUnicidade: null,
      erroItens: null,
      errosDuplicatas: [],
      errosConflitosGlobais: [],
    };
  },

  computed: {
    isInfantil() {
      return this.form.serie && this.form.serie.curso_id === 1;
    },
  },

  async mounted() {
    await this.carregarSeries();
    await this.carregarEtapas();
    if (this.editing && this.grupoId) {
      await this.carregarGrupo();
    }
  },

  methods: {
    async carregarSeries() {
      this.carregandoSeries = true;
      try {
        this.series = await this.$services.seriesService.syncAll();
      } catch (e) {
        this.$handleError(e);
      } finally {
        this.carregandoSeries = false;
      }
    },

    async carregarEtapas() {
      this.carregandoEtapas = true;
      try {
        this.etapas = await this.$services.etapasService.getEtapasEntrada();
      } catch (e) {
        this.$handleError(e);
      } finally {
        this.carregandoEtapas = false;
      }
    },

    async onSerieChange(serie) {
      this.form.disciplina = null;
      this.form.etapa = null;
      this.form.itens = [];
      this.erroUnicidade = null;
      this.disciplinas = [];
      if (this.$refs.obsStep2) this.$refs.obsStep2.reset();
      if (this.$refs.obsStep3) this.$refs.obsStep3.reset();

      if (!serie) return;

      this.carregandoDisciplinas = true;
      try {
        this.disciplinas = await this.$services.codigosHabilidadesService.disciplinasPorSerie(
          serie.id
        );
      } catch (e) {
        this.$handleError(e);
      } finally {
        this.carregandoDisciplinas = false;
      }
    },

    onDisciplinaChange() {
      this.form.etapa = null;
      this.erroUnicidade = null;
      if (this.$refs.obsStep3) this.$refs.obsStep3.reset();
    },

    async avancarStep1() {
      const valido = await this.$refs.obsStep1.validate();
      if (valido) this.step = 2;
    },

    async avancarStep2() {
      const valido = await this.$refs.obsStep2.validate();
      if (valido) this.step = 3;
    },

    async avancarParaItens() {
      const valido = await this.$refs.obsStep3.validate();
      if (!valido) return;

      this.erroUnicidade = null;
      this.verificandoUnicidade = true;
      try {
        const excludeId = this.editing ? this.grupoId : null;
        const resultado = await this.$services.codigosHabilidadesService.verificarUnicidade(
          this.form.serie.id,
          this.form.disciplina.id,
          this.form.etapa.id,
          excludeId
        );
        if (resultado.exists) {
          this.erroUnicidade =
            "Já existe uma configuração para esta combinação de série, disciplina e etapa.";
          return;
        }
        if (!this.editing && this.form.itens.length === 0) {
          this.form.itens = this.criarItensIniciais();
        }
        this.step = 4;
      } catch (e) {
        this.$handleError(e);
      } finally {
        this.verificandoUnicidade = false;
      }
    },

    async salvar() {
      this.erroItens = null;

      const validoForm = await this.$refs.obsStep4.validate();
      if (!validoForm) return;

      if (!this.form.itens || this.form.itens.length === 0) {
        this.erroItens = "Adicione ao menos um item antes de salvar.";
        return;
      }

      if (this.temItensVazios(this.form.itens)) {
        this.erroItens = "Preencha todos os campos obrigatórios (*) nos itens antes de salvar.";
        return;
      }

      const dupsErros = this.validarDuplicatas(this.form.itens, this.isInfantil);
      if (dupsErros.length) {
        this.errosDuplicatas = dupsErros;
        return;
      }
      this.errosDuplicatas = [];

      const codigos = this.extrairCodigos(this.form.itens);
      const excludeId = this.editing ? this.grupoId : null;
      const { conflitos } = await this.$services.codigosHabilidadesService.checkCodigos(
        codigos,
        excludeId
      );
      if (conflitos.length) {
        this.errosConflitosGlobais = conflitos;
        return;
      }
      this.errosConflitosGlobais = [];

      this.salvando = true;
      try {
        const payload = {
          serie_id: this.form.serie.id,
          disciplina_id: this.form.disciplina.id,
          etapa_id: this.form.etapa.id,
          itens: this.form.itens,
        };

        if (this.editing) {
          await this.$services.codigosHabilidadesService.atualizar(this.grupoId, payload);
          this.$toast.success("Configuração atualizada com sucesso!");
        } else {
          await this.$services.codigosHabilidadesService.salvar(payload);
          this.$toast.success("Configuração criada com sucesso!");
        }

        this.$router.push({ name: "codigosHabilidades.index" });
      } catch (e) {
        this.$handleError(e);
      } finally {
        this.salvando = false;
      }
    },

    novoItem(extra = {}) {
      return { descricao: "", apelido: null, ordem: 0, children: [], isNovo: true, ...extra };
    },

    criarItensIniciais() {
      if (this.isInfantil) {
        const objetivo = this.novoItem({ ordem: 0 });
        const raiz = this.novoItem({ ordem: 0, children: [objetivo] });
        return [raiz];
      }
      const habilidade = this.novoItem({ ordem: 0 });
      const objeto = this.novoItem({ ordem: 0, children: [habilidade] });
      const raiz = this.novoItem({ ordem: 0, children: [objeto] });
      return [raiz];
    },

    extrairCodigos(itens) {
      const codigos = [];
      const coletar = (lista) => {
        lista.forEach((item) => {
          if (item.apelido && item.apelido.trim()) codigos.push(item.apelido.trim());
          if (item.children && item.children.length) coletar(item.children);
        });
      };
      coletar(itens);
      return [...new Set(codigos)];
    },

    validarDuplicatas(itens, isInfantil) {
      const labelN0 = isInfantil ? "Campo de Experiência" : "Unidade Temática";
      const labelN1 = isInfantil ? "Objetivo de Aprendizagem" : "Objeto de Conhecimento";
      const erros = [];

      const descs0 = itens.map((i) => i.descricao?.trim()).filter(Boolean);
      const dups0 = descs0.filter((v, i) => descs0.indexOf(v) !== i);
      if (dups0.length) erros.push(`${labelN0} duplicado: ${[...new Set(dups0)].join(", ")}`);

      const todosN1 = itens.flatMap((item) => item.children || []);

      if (isInfantil) {
        const cods = todosN1.map((c) => c.apelido?.trim()).filter(Boolean);
        const dups = cods.filter((v, j) => cods.indexOf(v) !== j);
        if (dups.length)
          erros.push(`Código duplicado em ${labelN1}: ${[...new Set(dups)].join(", ")}`);
      } else {
        const descs = todosN1.map((c) => c.descricao?.trim()).filter(Boolean);
        const dups = descs.filter((v, j) => descs.indexOf(v) !== j);
        if (dups.length) erros.push(`${labelN1} duplicado: ${[...new Set(dups)].join(", ")}`);

        const todosN2 = todosN1.flatMap((c) => c.children || []);
        const cods2 = todosN2.map((c) => c.apelido?.trim()).filter(Boolean);
        const dups2 = cods2.filter((v, k) => cods2.indexOf(v) !== k);
        if (dups2.length)
          erros.push(`Código duplicado em Habilidade: ${[...new Set(dups2)].join(", ")}`);
      }

      return erros;
    },

    temItensVazios(itens) {
      return itens.some(
        (item) =>
          !item.descricao ||
          !item.descricao.trim() ||
          (item.children && this.temItensVazios(item.children))
      );
    },

    async carregarGrupo() {
      this.$loaderService.open("Carregando configuração...");
      try {
        const grupo = await this.$services.codigosHabilidadesService.buscarPorId(this.grupoId);

        this.form.serie = grupo.serie;
        if (grupo.serie) {
          this.carregandoDisciplinas = true;
          try {
            this.disciplinas = await this.$services.codigosHabilidadesService.disciplinasPorSerie(
              grupo.serie.id
            );
          } finally {
            this.carregandoDisciplinas = false;
          }
        }

        this.form.disciplina = grupo.disciplina;
        this.form.etapa = grupo.etapa;
        this.form.itens = this.converterItens(grupo.root_itens || []);
        this.step = 4;
      } catch (e) {
        this.$handleError(e);
      } finally {
        this.$loaderService.close();
      }
    },

    converterItens(itens) {
      return (itens || []).map((item) => ({
        descricao: item.descricao || "",
        apelido: item.apelido || null,
        ordem: item.ordem || 0,
        children: this.converterItens(item.children || []),
      }));
    },
  },
};
</script>
